import { redactUrlSecrets, redactUrlSecretsIn } from "@pontimed/analytics/redact-url";
import * as Sentry from "@sentry/react";

/**
 * F6 (launch-readiness): browser Sentry for the SPA. DSNs are public by
 * design — the prod fallback below is the same value already exposed via
 * NEXT_PUBLIC_SENTRY_DSN on the marketing site, so CF Pages needs no env
 * change. Dev stays silent unless VITE_SENTRY_DSN is set locally.
 */
const PROD_DSN =
  "https://e607379433ec55b702c23da8e9f0cd93@o4511285765013504.ingest.de.sentry.io/4511286005399632";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  const dsn =
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
    (import.meta.env.PROD ? PROD_DSN : undefined);
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // PHI-safe defaults: no IP/cookie auto-capture; URLs may carry ids but
    // never health payloads (those travel in POST bodies, not captured).
    sendDefaultPii: false,
    beforeBreadcrumb(breadcrumb) {
      // Drop console noise; keep navigation + fetch status lines.
      if (breadcrumb.category === "console") return null;
      // A one-tap security link's `token` never leaves the browser — the
      // navigation that drops it from the address bar names it in `from`.
      redactUrlSecretsIn(breadcrumb.data);
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request?.url) event.request.url = redactUrlSecrets(event.request.url);
      if (typeof event.request?.query_string === "string") {
        event.request.query_string = redactUrlSecrets(`?${event.request.query_string}`).slice(1);
      }
      for (const breadcrumb of event.breadcrumbs ?? []) redactUrlSecretsIn(breadcrumb.data);
      return event;
    },
  });
  initialized = true;
}

/** RootError bridge — report and rethrow nothing (UI already shows fallback). */
export function captureRootError(error: unknown): void {
  if (!initialized) return;
  Sentry.captureException(error, { tags: { surface: "root-error-boundary" } });
}
